import { writable, get } from 'svelte/store';

/**
 * Store for caching claim validation results to avoid redundant backend calls.
 * This prevents blocking when the backend is busy with heavy operations like ML term analysis.
 *
 * IMPORTANT: Cache is cleared on page refresh to ensure fresh validation.
 */
function createValidationCacheStore() {
    const { subscribe, set, update } = writable({
        lastClaimsContent: null,  // The claims content that was last validated
        lastValidationResult: null,  // The cached validation result
        lastValidationTime: null,  // When the validation was performed
        isValidating: false,  // Whether validation is currently in progress
        validationRequired: true  // Whether validation is required (blocking by default)
    });

    return {
        subscribe,
        
        /**
         * Get cached validation if claims haven't changed
         * @param {string} claimsContent - Current claims content
         * @returns {Object|null} Cached validation result or null if cache miss
         */
        getCachedValidation(claimsContent) {
            const state = get({ subscribe });
            
            // Filter out comment lines for comparison (same logic as toolbar)
            const actualClaims = claimsContent.split('\n')
                .filter(line => !line.trimStart().startsWith('#'))
                .join('\n')
                .trim();
            
            // Check if claims content matches cached content
            if (state.lastClaimsContent === actualClaims && state.lastValidationResult) {
                console.log('[ValidationCache] Cache hit - returning cached validation');
                return state.lastValidationResult;
            }
            
            console.log('[ValidationCache] Cache miss - claims have changed');
            return null;
        },
        
        /**
         * Store validation result in cache
         * @param {string} claimsContent - Claims content that was validated
         * @param {Object} validationResult - Validation result from backend
         */
        setCachedValidation(claimsContent, validationResult) {
            // Filter out comment lines for storage
            const actualClaims = claimsContent.split('\n')
                .filter(line => !line.trimStart().startsWith('#'))
                .join('\n')
                .trim();
            
            update(state => ({
                ...state,
                lastClaimsContent: actualClaims,
                lastValidationResult: validationResult,
                lastValidationTime: Date.now(),
                isValidating: false
            }));
            
            console.log('[ValidationCache] Cached validation result');
        },
        
        /**
         * Mark that validation is in progress
         */
        setValidating(isValidating) {
            update(state => ({
                ...state,
                isValidating
            }));
        },
        
        /**
         * Clear the cache (resets to blocking default state)
         */
        clear() {
            set({
                lastClaimsContent: null,
                lastValidationResult: null,
                lastValidationTime: null,
                isValidating: false,
                validationRequired: true
            });
            console.log('[ValidationCache] Cache cleared - validation required (blocking mode)');
        },
        
        /**
         * Check if validation is currently in progress
         */
        isValidating() {
            const state = get({ subscribe });
            return state.isValidating;
        },

        /**
         * Check if validation is required (blocking mode)
         */
        isValidationRequired() {
            const state = get({ subscribe });
            return state.validationRequired;
        },

        /**
         * Mark validation as no longer required (after successful validation)
         */
        markValidationComplete() {
            update(state => ({
                ...state,
                validationRequired: false
            }));
        }
    };
}

export const validationCacheStore = createValidationCacheStore();

// Clear cache on page load to ensure fresh validation
if (typeof window !== 'undefined') {
    validationCacheStore.clear();
    console.log('[ValidationCache] Initialized - cache cleared on page load');
}